import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: "Helvetica",
    fontSize: 10,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 30,
  },
  companyInfo: {
    textAlign: "right",
    flexGrow: 1,
    fontSize: 11,
  },
  title: {
    fontSize: 20,
    marginBottom: 20,
    textAlign: "center",
  },
  customerInfo: {
    marginBottom: 20,
  },
  infoRow: {
    flexDirection: "row",
    marginBottom: 5,
  },
  label: {
    width: 120,
    fontWeight: "bold",
    fontSize: 10,
  },
  value: {
    flex: 1,
    fontSize: 10,
  },
  missingValue: {
    flex: 1,
    fontStyle: "italic",
    color: "#888888",
    fontSize: 10,
  },
  table: {
    marginTop: 20,
  },
  tableHeader: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#24e4ff",
    paddingBottom: 5,
    marginBottom: 10,
    padding: 8,
    fontSize: 10,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#ddd",
    paddingVertical: 8,
    fontSize: 10,
  },
  col1: {
    flex: 2,
  },
  col2: {
    flex: 1,
    textAlign: "center",
  },
  col3: {
    flex: 1,
    textAlign: "right",
  },
  col4: {
    flex: 1,
    textAlign: "center",
  },
  summary: {
    marginTop: 30,
    borderTopWidth: 1,
    borderTopColor: "#24e4ff",
    paddingTop: 10,
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 5,
    fontSize: 10,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginTop: 20,
    marginBottom: 10,
    color: "#24e4ff",
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: "#ddd",
    marginVertical: 10,
  },
  noData: {
    textAlign: "center",
    fontStyle: "italic",
    color: "#888888",
    padding: 10,
    fontSize: 10,
  },
});

const InvoicePDF = ({
  invoice = {},
  stats = {},
  customer = {},
  pricelist = {},
  aggregateParts = undefined,
  showFullPartNumbers = undefined,
}) => {
  const shouldAggregate = aggregateParts !== undefined ? aggregateParts : false;
  const shouldShowFullPartNumbers =
    showFullPartNumbers !== undefined ? showFullPartNumbers : true;

  const parts = invoice?.parts?.items || [];

  // Function to aggregate parts if the setting is enabled
  const processParts = (parts, grade = null) => {
    // First, filter by grade if specified
    let filteredParts = parts;
    if (grade !== null) {
      filteredParts = parts.filter((part) => part.grade === grade);
    }

    // If aggregation is not enabled, just return the filtered parts
    if (!shouldAggregate) {
      return filteredParts;
    }

    // Aggregate parts with the same part number
    const partMap = new Map();

    filteredParts.forEach((part) => {
      const key = `${part.partNumber}-${part.grade}`;
      if (partMap.has(key)) {
        const existing = partMap.get(key);
        existing.quantity += 1;
        existing.totalPrice += parseFloat(part.price || 0);
      } else {
        partMap.set(key, {
          ...part,
          quantity: 1,
          totalPrice: parseFloat(part.price || 0),
        });
      }
    });

    return Array.from(partMap.values());
  };

  // Process parts based on category
  // Core catalytic parts exclude aftermarket and empty
  const coreCatalyticParts = parts.filter(
    (part) =>
      part.status !== "unit_by_weight" &&
      part.status !== "aftermarket" &&
      part.status !== "empty" &&
      String(part.partNumber || "")
        .trim()
        .toLowerCase() !== "after market"
  );

  const wholeParts = processParts(coreCatalyticParts, 1);
  const halfParts = processParts(coreCatalyticParts, 0.5);
  const nonCatalyticParts = processParts(
    parts.filter((part) => part.status === "unit_by_weight")
  );
  const aftermarketParts = processParts(
    parts.filter(
      (part) =>
        part.status === "aftermarket" ||
        String(part.partNumber || "")
          .trim()
          .toLowerCase() === "after market"
    )
  );
  const emptyParts = processParts(
    parts.filter((part) => part.status === "empty")
  );

  // Helper function to display value or placeholder for missing data
  const displayValue = (value, placeholder = "(Not Entered)") => {
    return value ? (
      <Text style={styles.value}>{value}</Text>
    ) : (
      <Text style={styles.missingValue}>{placeholder}</Text>
    );
  };

  // Helper function to mask part numbers if needed
  const getPartNumberDisplay = (partNumber) => {
    if (shouldShowFullPartNumbers) return partNumber || "(No Part #)";

    // Find the index of the first space
    const spaceIndex = partNumber ? partNumber.indexOf(" ") : -1;

    // If there's no space, return the first few characters and then mask the rest
    if (spaceIndex === -1) {
      if (!partNumber) return "(No Part #)";
      const visiblePart = partNumber.substring(
        0,
        Math.min(3, partNumber.length)
      );
      return partNumber.length > 3 ? `${visiblePart} XXXX` : partNumber;
    }

    // Otherwise, show everything before the first space and mask the rest
    return `${partNumber.substring(0, spaceIndex)} XXXX`;
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header removed per request */}

        <View style={styles.customerInfo}>
          <View style={styles.infoRow}>
            <Text style={styles.label}>Customer/Company:</Text>
            {displayValue(
              customer?.companyName ||
                customer?.username ||
                invoice?.companyName ||
                invoice?.customerName
            )}
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.label}>Price List:</Text>
            {displayValue(pricelist?.name || invoice?.priceList)}
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.label}>Date:</Text>
            {displayValue(
              new Date(invoice?.createdAt || new Date()).toLocaleDateString(
                undefined,
                { year: "numeric", month: "long", day: "numeric" }
              )
            )}
          </View>
          {shouldAggregate && (
            <View style={styles.infoRow}>
              <Text style={styles.label}>Parts Display:</Text>
              <Text style={styles.value}>Aggregated by Quantity</Text>
            </View>
          )}
        </View>

        <Text style={styles.sectionTitle}>
          Whole Units (
          {coreCatalyticParts.filter((part) => part.grade === 1).length})
        </Text>
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={styles.col1}>Part Number</Text>
            {shouldAggregate && <Text style={styles.col4}>Qty</Text>}
            <Text style={styles.col2}>Grade</Text>
            <Text style={styles.col3}>Price</Text>
          </View>

          {wholeParts.length > 0 ? (
            wholeParts.map((part, index) => (
              <View key={`whole-${index}`} style={styles.tableRow}>
                <Text style={styles.col1}>
                  {getPartNumberDisplay(part?.partNumber)}
                </Text>
                {shouldAggregate && (
                  <Text style={styles.col4}>{part.quantity}</Text>
                )}
                <Text style={styles.col2}>Full</Text>
                <Text style={styles.col3}>
                  $
                  {shouldAggregate && part.quantity > 1
                    ? part.totalPrice.toFixed(2)
                    : parseFloat(part?.price || 0).toFixed(2)}
                </Text>
              </View>
            ))
          ) : (
            <View style={styles.tableRow}>
              <Text style={styles.noData}>No whole units</Text>
            </View>
          )}
        </View>

        <Text style={styles.sectionTitle}>
          Half Units (
          {coreCatalyticParts.filter((part) => part.grade === 0.5).length})
        </Text>
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={styles.col1}>Part Number</Text>
            {shouldAggregate && <Text style={styles.col4}>Qty</Text>}
            <Text style={styles.col2}>Grade</Text>
            <Text style={styles.col3}>Price</Text>
          </View>

          {halfParts.length > 0 ? (
            halfParts.map((part, index) => (
              <View key={`half-${index}`} style={styles.tableRow}>
                <Text style={styles.col1}>
                  {getPartNumberDisplay(part?.partNumber)}
                </Text>
                {shouldAggregate && (
                  <Text style={styles.col4}>{part.quantity}</Text>
                )}
                <Text style={styles.col2}>Half</Text>
                <Text style={styles.col3}>
                  $
                  {shouldAggregate && part.quantity > 1
                    ? part.totalPrice.toFixed(2)
                    : parseFloat(part?.price || 0).toFixed(2)}
                </Text>
              </View>
            ))
          ) : (
            <View style={styles.tableRow}>
              <Text style={styles.noData}>No half units</Text>
            </View>
          )}
        </View>

        {nonCatalyticParts.length > 0 && (
          <>
            <Text style={styles.sectionTitle}>
              Non-Catalytics (
              {parts.filter((part) => part.status === "unit_by_weight").length})
            </Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={styles.col1}>Material</Text>
                {shouldAggregate && <Text style={styles.col4}>Qty</Text>}
                <Text style={styles.col2}>Weight</Text>
                <Text style={styles.col3}>Price</Text>
              </View>

              {nonCatalyticParts.map((part, index) => (
                <View key={`non-cat-${index}`} style={styles.tableRow}>
                  <Text style={styles.col1}>
                    {getPartNumberDisplay(part?.partNumber)}
                  </Text>
                  {shouldAggregate && (
                    <Text style={styles.col4}>{part.quantity}</Text>
                  )}
                  <Text style={styles.col2}>{part?.cost || "-"}</Text>
                  <Text style={styles.col3}>
                    $
                    {shouldAggregate && part.quantity > 1
                      ? part.totalPrice.toFixed(2)
                      : parseFloat(part?.price || 0).toFixed(2)}
                  </Text>
                </View>
              ))}
            </View>
          </>
        )}

        {aftermarketParts.length > 0 && (
          <>
            <Text style={styles.sectionTitle}>
              Aftermarkets (
              {
                parts.filter(
                  (part) =>
                    part.status === "aftermarket" ||
                    String(part.partNumber || "")
                      .trim()
                      .toLowerCase() === "after market"
                ).length
              }
              )
            </Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={styles.col1}>Part Number</Text>
                {shouldAggregate && <Text style={styles.col4}>Qty</Text>}
                <Text style={styles.col2}>Grade</Text>
                <Text style={styles.col3}>Price</Text>
              </View>
              {aftermarketParts.map((part, index) => (
                <View key={`aftermarket-${index}`} style={styles.tableRow}>
                  <Text style={styles.col1}>
                    {getPartNumberDisplay(part?.partNumber)}
                  </Text>
                  {shouldAggregate && (
                    <Text style={styles.col4}>{part.quantity}</Text>
                  )}
                  <Text style={styles.col2}>
                    {parseFloat(part?.grade) === 0.5 ? "Half" : "Full"}
                  </Text>
                  <Text style={styles.col3}>
                    $
                    {shouldAggregate && part.quantity > 1
                      ? part.totalPrice.toFixed(2)
                      : parseFloat(part?.price || 0).toFixed(2)}
                  </Text>
                </View>
              ))}
            </View>
          </>
        )}

        {emptyParts.length > 0 && (
          <>
            <Text style={styles.sectionTitle}>
              Empties ({parts.filter((part) => part.status === "empty").length})
            </Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={styles.col1}>Part Number</Text>
                {shouldAggregate && <Text style={styles.col4}>Qty</Text>}
                <Text style={styles.col2}>Grade</Text>
                <Text style={styles.col3}>Price</Text>
              </View>
              {emptyParts.map((part, index) => (
                <View key={`empty-${index}`} style={styles.tableRow}>
                  <Text style={styles.col1}>
                    {getPartNumberDisplay(part?.partNumber)}
                  </Text>
                  {shouldAggregate && (
                    <Text style={styles.col4}>{part.quantity}</Text>
                  )}
                  <Text style={styles.col2}>
                    {parseFloat(part?.grade) === 0.5 ? "Half" : "Full"}
                  </Text>
                  <Text style={styles.col3}>
                    $
                    {shouldAggregate && part.quantity > 1
                      ? part.totalPrice.toFixed(2)
                      : parseFloat(part?.price || 0).toFixed(2)}
                  </Text>
                </View>
              ))}
            </View>
          </>
        )}

        <View style={styles.divider} />

        <View style={styles.summary}>
          <View style={styles.summaryRow}>
            <Text>Total Units:</Text>
            <Text>{parts.length || stats?.totalUnits || 0}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text>Whole Units:</Text>
            <Text>
              {parts.filter((part) => part.grade === 1).length ||
                stats?.wholeUnits ||
                0}
            </Text>
          </View>
          <View style={styles.summaryRow}>
            <Text>Half Units:</Text>
            <Text>
              {parts.filter((part) => part.grade === 0.5).length ||
                stats?.halfUnits ||
                0}
            </Text>
          </View>
          {nonCatalyticParts.length > 0 && (
            <View style={styles.summaryRow}>
              <Text>Non-Catalytics:</Text>
              <Text>
                {
                  parts.filter((part) => part.status === "unit_by_weight")
                    .length
                }
              </Text>
            </View>
          )}
          <View style={styles.summaryRow}>
            <Text>Average Per Unit (w/o aftermarkets):</Text>
            <Text>${(stats?.averagePerUnit || 0).toFixed(2)}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text>Average Per Unit (w/ aftermarkets):</Text>
            <Text>
              ${(stats?.averagePerUnitWithAftermarkets || 0).toFixed(2)}
            </Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={{ fontWeight: "bold" }}>Total Value:</Text>
            <Text style={{ fontWeight: "bold" }}>
              ${(stats?.currentTotal || 0).toFixed(2)}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default InvoicePDF;
